/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { chunk } from 'e';
import { ItemBank } from 'oldschooljs/dist/meta/types';

import { SpriteSheetItemData, SpriteSheetData } from 'meta/types';
import spriteSheetImage from 'resources/spritesheet.png';
import spriteSheetData from 'resources/spritesheet.json';
import bankBgRepeater from 'resources/images/bankBgRepeater.png';
import borderCorner from 'resources/bank_border_c.png';
import borderSide from 'resources/bank_border_v.png';
import borderTop from 'resources/bank_border_h.png';
import useImage from './useImage';
import { generateHexColorForCashStack, formatItemStackQuantity, toKMB } from 'meta/util';
import 'resources/osrsFonts.css';
import { Bank } from 'oldschooljs';
import useSize from '@react-hook/size';

interface Props {
	bank: ItemBank;
	title: string | null;
	showPrice: boolean;
}

function drawBorder(
	canvas: HTMLCanvasElement,
	corner: HTMLImageElement,
	top: HTMLImageElement,
	side: HTMLImageElement
) {
	const ctx = canvas.getContext('2d', { alpha: false })!;
	// Draw top border
	ctx.fillStyle = ctx.createPattern(top, 'repeat-x')!;
	ctx.fillRect(0, 0, canvas.width, top!.height);

	// Draw bottom border
	ctx.save();
	ctx.fillStyle = ctx.createPattern(top, 'repeat-x')!;
	ctx.translate(0, canvas.height);
	ctx.scale(1, -1);
	ctx.fillRect(0, 0, canvas.width, top!.height);
	ctx.restore();

	// Draw title line
	if (1) {
		ctx.save();
		ctx.fillStyle = ctx.createPattern(top, 'repeat-x')!;
		ctx.translate(side!.width, 27);
		ctx.fillRect(0, 0, canvas.width, top!.height);
		ctx.restore();
	}

	// Draw left border
	ctx.save();
	ctx.fillStyle = ctx.createPattern(side, 'repeat-y')!;
	ctx.translate(0, side!.width);
	ctx.fillRect(0, 0, side!.width, canvas.height);
	ctx.restore();

	// Draw right border
	ctx.fillStyle = ctx.createPattern(side, 'repeat-y')!;
	ctx.save();
	ctx.translate(canvas.width, 0);
	ctx.scale(-1, 1);
	ctx.fillRect(0, 0, side!.width, canvas.height);
	ctx.restore();

	// Draw corner borders
	// Top left
	ctx.save();
	ctx.translate(0, 0);
	ctx.scale(1, 1);
	ctx.drawImage(corner, 0, 0);
	ctx.restore();

	// Top right
	ctx.save();
	ctx.translate(canvas.width, 0);
	ctx.scale(-1, 1);
	ctx.drawImage(corner, 0, 0);
	ctx.restore();

	// Bottom right
	ctx.save();
	ctx.translate(canvas.width, canvas.height);
	ctx.scale(-1, -1);
	ctx.drawImage(corner, 0, 0);
	ctx.restore();

	// Bottom left
	ctx.save();
	ctx.translate(0, canvas.height);
	ctx.scale(1, -1);
	ctx.drawImage(corner, 0, 0);
	ctx.restore();
}

const CanvasContainer = styled.div`
	image-rendering: pixelated;
	width: 100%;
	height: 100%;
	font-smooth: never;
	display: flex;
	justify-content: center;
	align-items: center;
	align-content: center;
	canvas {
		image-rendering: pixelated;
		-webkit-font-smoothing: none;
		image-rendering: optimizeSpeed; /* Older versions of FF          */
		image-rendering: -moz-crisp-edges; /* FF 6.0+                       */
		image-rendering: -webkit-optimize-contrast; /* Safari                        */
		image-rendering: -o-crisp-edges; /* OS X & Windows Opera (12.02+) */
		image-rendering: pixelated; /* Awesome future-browsers       */
		-ms-interpolation-mode: nearest-neighbor;
	}
`;

const spacer = 22;
const itemSize = 32;
const distanceFromEdge = spacer * 0.6;

function drawBank(
	canvas: HTMLCanvasElement,
	spritesheet: HTMLImageElement,
	repeaterImage: HTMLImageElement,
	bank: ItemBank,
	width: number,
	corner: HTMLImageElement,
	top: HTMLImageElement,
	side: HTMLImageElement,
	title: string | null,
	showPrice: boolean
) {
	const scale = window.devicePixelRatio;

	const osBank = new Bank(bank);
	const bankEntries = osBank.items().sort((b, a) => a[0].price * a[1] - b[0].price * b[1]);

	const columns = Math.floor((width - distanceFromEdge * 2) / (itemSize + spacer * 2));
	const chunkedLoot = chunk(bankEntries, Math.max(columns, 1));
	const rows = chunkedLoot.length;

	canvas.width = width * scale;
	canvas.height = Math.max(95, (rows * (itemSize + spacer) + distanceFromEdge * 2) * scale);

	const ctx = canvas.getContext('2d', { alpha: false })!;

	ctx.scale(scale, scale);
	ctx.imageSmoothingEnabled = false;
	ctx.font = `${16}px "OSRSFontCompact Regular"`;
	ctx.textAlign = 'start';
	ctx.fillStyle = '#494034';

	const ptrn = ctx.createPattern(repeaterImage, 'repeat')!;
	ctx.fillStyle = ptrn;
	ctx.fillRect(0, 0, canvas.width * 2, canvas.height * 2);

	for (let i = 0; i < chunkedLoot.length; i++) {
		for (let x = 0; x < chunkedLoot[i].length; x++) {
			const [item, quantity] = chunkedLoot[i][x];
			const dataForItem: SpriteSheetItemData = (spriteSheetData as SpriteSheetData)[item.id]!;

			if (quantity < 1) continue;

			const spacing = spacer + distanceFromEdge;

			const xLoc = Math.floor(spacing + x * (width / columns));
			const yLoc = Math.floor(spacing + i * (itemSize + spacer / 2)) + 12;

			ctx.drawImage(
				spritesheet,

				dataForItem.x,
				dataForItem.y,

				dataForItem.w,
				dataForItem.h,

				xLoc + (itemSize - dataForItem.w) / 2,
				yLoc + (itemSize - dataForItem.h) / 2,

				dataForItem.w,
				dataForItem.h
			);

			const quantityColor = generateHexColorForCashStack(quantity);
			const formattedQuantity = formatItemStackQuantity(quantity);

			const textXLoc = xLoc - 3;
			const textYLoc = yLoc + 6;

			ctx.fillStyle = '#101010';
			ctx.fillText(formattedQuantity, textXLoc + 1, textYLoc + 1);

			ctx.fillStyle = quantityColor;
			ctx.fillText(formattedQuantity, textXLoc, textYLoc);

			if (showPrice) {
				const value = item.price * quantity;
				const fmted = toKMB(value);
				const valueColor = generateHexColorForCashStack(value);
				ctx.fillStyle = '#101010';
				ctx.fillText(fmted, textXLoc + 1, textYLoc + 24 + 1);

				ctx.fillStyle = valueColor;
				ctx.fillText(fmted, textXLoc, textYLoc + 24);
			}
		}
	}

	drawBorder(canvas, corner, top, side);

	if (title) {
		// Draw Bank Title
		ctx.textAlign = 'center';
		ctx.font = ` 16px "RuneScape Bold 12"`;

		ctx.fillStyle = '#000000';
		ctx.fillText(title, Math.floor(canvas.width / 2) + 1, 22);

		ctx.fillStyle = '#ff981f';
		ctx.fillText(title, Math.floor(canvas.width / 2), 22);
	}
}

const BankImage: React.FC<Props> = ({ bank, title, showPrice }) => {
	const canvasRef = useRef<HTMLCanvasElement>(null);
	const containerRef = useRef<HTMLDivElement>(null);

	const [fontsLoaded, setFontsLoaded] = useState<boolean>(false);
	const [width] = useSize(containerRef);

	const [repeaterImage] = useImage(bankBgRepeater);
	const [spritesheet] = useImage(spriteSheetImage);
	const [corner] = useImage(borderCorner);
	const [top] = useImage(borderTop);
	const [side] = useImage(borderSide);

	useEffect(() => {
		if (fontsLoaded) return;
		async function loadFonts() {
			await Promise.all(
				['OSRSFontCompact Regular', 'RuneScape Bold 12'].map((name) =>
					document.fonts.load(`16px "${name}"`)
				)
			).then(console.log);
			setFontsLoaded(true);
		}
		loadFonts();
	}, [setFontsLoaded]);

	useEffect(() => {
		if (fontsLoaded && repeaterImage && spritesheet && canvasRef && canvasRef.current) {
			drawBank(
				canvasRef.current,
				spritesheet,
				repeaterImage,
				bank,
				Math.min(1000, width),
				corner!,
				top!,
				side!,
				title,
				showPrice
			);
		} else {
			console.log(`Not drawing ${bank}`);
		}
	}, [
		repeaterImage,
		spritesheet,
		bank,
		canvasRef,
		fontsLoaded,
		corner,
		top,
		side,
		width,
		showPrice
	]);

	return (
		<CanvasContainer className="bank-container" id="bank" ref={containerRef}>
			{repeaterImage && spritesheet && corner && top && side && <canvas ref={canvasRef} />}
		</CanvasContainer>
	);
};

export default BankImage;
